import styled from '@emotion/styled';
import { css } from '@emotion/core';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { gutters } from 'app/spacing';
import { ApplySelectOverlayButton } from 'components/Inputs/SelectOverlay';
import { toDateInTimezone } from 'app/timezone';
import { getCellForFieldType } from 'components/Layout/BigTableLayout/InlineCells';
import { isStandardObject } from 'components/Modals/utilities';
import { TextEllipsis, TextEllipsisWithTooltip } from 'components/Kizen/Table';
import { getRelatedParam } from 'components/Charts/ScheduledActivities/helpers';
import Tooltip from 'components/Kizen/Tooltip';
import Icon from 'components/Kizen/Icon';
import IconButton from 'components/Kizen/IconButton';
import DateTimeInputInline from 'components/Inputs/inline/DateTimeInput';
import { ColumnsIconCell } from 'pages/Common/components/BigTableCells';
import BubbleProfileImage from 'components/Charts/ScheduledActivities/BubbleProfileImage';
import CustomObjectsService from 'services/CustomObjectsService';
import PipelineService from 'services/PipelineService';
import { getObjectUrl } from 'services/FieldService';
import { FIELD_TYPES } from 'utility/constants';
import SortableHeadCell from '__pages/ActivitySettings/pages/ScheduledAndLogged/components/SortableHeadCell';
import SizedCell from '__pages/ActivitySettings/pages/ScheduledAndLogged/components/SizedCell';
import ControlsCell from '__pages/ActivitySettings/pages/ScheduledAndLogged/components/ControlsCell';
import { DateTimeInputWrapper } from '__pages/ActivitySettings/pages/ScheduledAndLogged/styled';

export const NO_VALUE = '—';
export const PADDING = 30; // Right cell padding in pixels

export const SCHEDULED_ACTIVITIES_ACTIONS = {
  Complete: {
    value: 'complete',
    label: (t) => t('Complete'),
  },
  ViewNotes: {
    value: 'viewNotes',
    label: (t) => t('View Notes'),
  },
  HideNotes: {
    value: 'hideNotes',
    label: (t) => t('Hide Notes'),
  },
  Edit: {
    value: 'edit',
    label: (t) => t('Edit'),
  },
  Delete: { value: 'delete', label: (t) => t('Delete') },
  ResendWebhook: { value: 'resendWebhook', label: (t) => t('Resend Webhook') },
};
export const getSearchByParam = (
  activity_field_id,
  custom_object_id,
  custom_field_id
) => {
  if (activity_field_id) {
    return `activity_field_${activity_field_id}`;
  } else if (custom_object_id) {
    return `associated_custom_object_${custom_object_id}`;
  } else if (custom_field_id) {
    return `associated_custom_field_${custom_field_id}`;
  }
  return ``;
};

export const getActions = (access, showNotes, t) => {
  const defaultActions = [
    {
      ...SCHEDULED_ACTIVITIES_ACTIONS.Complete,
      label: SCHEDULED_ACTIVITIES_ACTIONS.Complete.label(t),
    },
    showNotes
      ? {
          ...SCHEDULED_ACTIVITIES_ACTIONS.HideNotes,
          label: SCHEDULED_ACTIVITIES_ACTIONS.HideNotes.label(t),
        }
      : {
          ...SCHEDULED_ACTIVITIES_ACTIONS.ViewNotes,
          label: SCHEDULED_ACTIVITIES_ACTIONS.ViewNotes.label(t),
        },
  ];

  return Object.keys(access).reduce((acc, el) => {
    if (el === 'remove' && access[el])
      return acc.concat({
        ...SCHEDULED_ACTIVITIES_ACTIONS.Delete,
        label: SCHEDULED_ACTIVITIES_ACTIONS.Delete.label(t),
      });

    if (el === 'edit' && access[el])
      return acc.concat({
        ...SCHEDULED_ACTIVITIES_ACTIONS.Edit,
        label: SCHEDULED_ACTIVITIES_ACTIONS.Edit.label(t),
      });

    return acc;
  }, defaultActions);
};

export const getLoggedActions = ({ submissionAction, t }) => {
  if (submissionAction === 'trigger_webhook') {
    return [
      {
        ...SCHEDULED_ACTIVITIES_ACTIONS.ResendWebhook,
        label: SCHEDULED_ACTIVITIES_ACTIONS.ResendWebhook.label(t),
      },
    ];
  }

  return [];
};

const formatTeamSelectorLabel = (value) =>
  `${value?.firstName} ${value?.lastName} ${value?.email ? `(${value?.email})` : ''}`;

const formatRelationshipLabel = (val) => ({
  ...val,
  label: val.name || formatTeamSelectorLabel(val),
});

export const getFieldDataValue = (fieldData, field) => {
  const { value } = fieldData;

  if (value?.name && !value?.label) {
    return { ...value, label: value.name };
  }

  if (!value) {
    return null;
  }

  switch (field?.fieldType) {
    case FIELD_TYPES.TeamSelector.type:
      return value?.label
        ? value
        : Array.isArray(value) && value.length > 0
          ? value.map((f) => ({
              ...f,
              label: formatTeamSelectorLabel(f),
            }))
          : {
              ...value,
              label: formatTeamSelectorLabel(value),
            };
    case FIELD_TYPES.Money.type:
      return value?.amount;
    case FIELD_TYPES.Checkboxes.type:
      return value.map((val) => val.id);
    case FIELD_TYPES.Relationship.type:
      if (value?.length > 1) {
        return value.map(formatRelationshipLabel);
      } else if (
        (field.relation.cardinality === 'one_to_one' ||
          field.relation.cardinality === 'many_to_one') &&
        value?.length === 1
      ) {
        return formatRelationshipLabel(value[0]);
      }
      break;
    default:
      break;
  }

  return value;
};

// The total width of the table is 1024px due to
// the size of this dashlet. These columns sizes are designed
// to match precisely, never permitting scrolling.
// TODO handle shrunken dashlet, in between desktop and mobile:
// currently it will scroll rather than scale down.
const columnSizes = {
  overdue: {
    default: {
      width: '1%',
      minWidth: '40px',
    },
    wide: {
      width: '1%',
      minWidth: '40px',
    },
  },
  overdueLogged: {
    default: {
      width: '1%',
      minWidth: '20px',
    },
    wide: {
      width: '1%',
      minWidth: '40px',
    },
  },
  activityType: {
    default: {
      minWidth: '185px',
      padding: '30px',
    },
    wide: {
      minWidth: '220px',
      padding: '30px',
    },
  },
  client: {
    default: {
      minWidth: '115px',
      padding: '30px',
    },
    wide: {
      minWidth: '140px',
      padding: '30px',
    },
  },
  related: {
    default: {
      minWidth: '115px',
      padding: '30px',
    },
    wide: {
      minWidth: '140px',
      padding: '30px',
    },
  },
  employee: {
    default: {
      minWidth: '80px',
      padding: '30px',
    },
  },
  loggedBy: {
    default: {
      minWidth: '80px',
      padding: '30px',
    },
  },
  scheduled: {
    default: {
      minWidth: '153px',
      padding: '30px',
    },
    wide: {
      minWidth: '153px',
      padding: '30px',
    },
  },
  loggedAt: {
    default: {
      minWidth: '153px',
      padding: '30px',
    },
    wide: {
      minWidth: '153px',
      padding: '30px',
    },
  },
  scheduledOn: {
    default: {
      minWidth: '153px',
      padding: '30px',
    },
    wide: {
      minWidth: '153px',
      padding: '30px',
    },
  },
  controls: {
    default: {
      width: '50px',
      padding: '4px', // 4px (of total 15px) moved into OptionalSpacer to account for whether scrollbar is appearing
    },
    wide: {
      width: '50px',
      padding: '4px',
    },
  },
  spacer: {
    // Collapses when scroll bar is present
    default: {
      width: '4px',
    },
    wide: {
      width: '4px',
    },
  },
};

export const StyledIconButton = styled(IconButton)`
  margin-right: ${gutters.spacing(2)}px;

  && svg {
    width: ${gutters.spacing(3)}px;
    height: ${gutters.spacing(3)}px;
  }

  ${({ zIndex }) =>
    typeof zIndex === 'number'
      ? css`
          z-index: ${zIndex};
        `
      : ''}
`;

const Hoverable = styled.span`
  cursor: pointer;
`;

export const ZIndexActionMenuWrapper = styled.div`
  z-index: 1;
`;

export const columnSize = (column, wide) => {
  return columnSizes[column] && columnSizes[column][wide ? 'wide' : 'default'];
};

const getDefaultScheduledColumns = ({
  onUpdateActivity,
  onResizeColumnWidth,
  onResizeStop,
  getColumnWidth,
  contactSortBy,
  t,
  canEdit = true,
  columnSettings,
}) => {
  const customLabels = columnSettings?.columns?.reduce((acc, column) => {
    if (
      column.id === 'activityType' ||
      column.id === 'client' ||
      column.id === 'employee' ||
      column.id === 'dueDateMyTime'
    ) {
      acc[column.id] = column.label;
      return acc;
    }
    return acc;
  }, {});

  const getLabel = (id, defaultLabel) => {
    return customLabels?.[id] || defaultLabel;
  };

  return {
    activityType: {
      headCell: (
        <SortableHeadCell
          {...columnSize('activityType')}
          label={getLabel('activityType', t('Activity Type'))}
          onResizeColumnWidth={onResizeColumnWidth}
          onResizeStop={onResizeStop}
          getColumnWidth={getColumnWidth}
          resizable={canEdit}
        />
      ),
      cell: (
        <SizedCell
          getColumnWidth={getColumnWidth}
          {...columnSize('activityType')}
        />
      ),
      format: (_, { activityObject }) => {
        return (
          <TextEllipsisWithTooltip data-qa={activityObject?.name || NO_VALUE}>
            {activityObject?.name || NO_VALUE}
          </TextEllipsisWithTooltip>
        );
      },
    },
    employee: {
      headCell: (
        <SortableHeadCell
          {...columnSize('employee')}
          onResizeColumnWidth={onResizeColumnWidth}
          onResizeStop={onResizeStop}
          getColumnWidth={getColumnWidth}
          label={getLabel('employee', t('Assignee'))}
          resizable={canEdit}
        />
      ),
      cell: (
        <SizedCell
          getColumnWidth={getColumnWidth}
          {...columnSize('employee')}
        />
      ),
      format: (value, activity) => {
        const fullName = activity.role
          ? activity.role?.name
          : value?.displayName;
        const imageURL = value?.picture?.thumbnailUrl || null;

        return (
          <>
            <Hoverable>
              <BubbleProfileImage
                fullName={fullName}
                imageURL={imageURL}
                showToolTip
                popperConfig={{
                  modifiers: {
                    offset: { offset: '0, -5px' }, // Touch top of text
                    preventOverflow: { enabled: false },
                  },
                }}
                value={activity.role || value || {}}
                activity={activity}
                onUpdateActivity={onUpdateActivity}
              />
            </Hoverable>
          </>
        );
      },
    },
    dueDateMyTime: {
      headCell: (
        <SortableHeadCell
          by="due_datetime_my_time"
          numeric
          {...columnSize('scheduled')}
          label={getLabel('dueDateMyTime', t('Due Date (My Time)'))}
          onResizeColumnWidth={onResizeColumnWidth}
          onResizeStop={onResizeStop}
          getColumnWidth={getColumnWidth}
          narrowestWidth="165"
          resizable={canEdit}
        />
      ),
      cell: (
        <SizedCell
          getColumnWidth={getColumnWidth}
          {...columnSize('scheduled')}
        />
      ),
      format: (_, activity) => {
        const { dueDatetime, access } = activity;
        if (!dueDatetime) {
          return <TextEllipsis>{NO_VALUE}</TextEllipsis>;
        }

        return (
          <DateTimeInputWrapper>
            <DateTimeInputInline
              inModal
              readOnly={!(access && access.edit)}
              value={new Date(dueDatetime)}
              onSubmit={(val) => {
                onUpdateActivity(activity, { dueDatetime: val.toISOString() });
              }}
              dateMenuProps={{
                menuRightButton: <ApplySelectOverlayButton />,
              }}
              timeMenuProps={{
                menuRightButton: <ApplySelectOverlayButton />,
              }}
              showDateTooltip
            />
          </DateTimeInputWrapper>
        );
      },
    },
    originalDueDatetime: {
      headCell: (
        <SortableHeadCell
          by="original_due_datetime"
          numeric
          {...columnSize('scheduled')}
          label={t('Original Due Date')}
          onResizeColumnWidth={onResizeColumnWidth}
          onResizeStop={onResizeStop}
          getColumnWidth={getColumnWidth}
          narrowestWidth="165"
          resizable={canEdit}
        />
      ),
      cell: (
        <SizedCell
          getColumnWidth={getColumnWidth}
          {...columnSize('scheduled')}
        />
      ),
      format: (_, activity) => {
        const { originalDueDatetime } = activity;
        if (!originalDueDatetime) {
          return <TextEllipsis>{NO_VALUE}</TextEllipsis>;
        }
        return (
          <DateTimeInputWrapper>
            <DateTimeInputInline
              inModal
              readOnly
              value={new Date(originalDueDatetime)}
              showDateTooltip
            />
          </DateTimeInputWrapper>
        );
      },
    },
    created: {
      headCell: (
        <SortableHeadCell
          by="created"
          numeric
          {...columnSize('scheduled')}
          label={getLabel('created', t('Created'))}
          onResizeColumnWidth={onResizeColumnWidth}
          onResizeStop={onResizeStop}
          getColumnWidth={getColumnWidth}
          narrowestWidth="165"
          resizable={canEdit}
        />
      ),
      cell: (
        <SizedCell
          getColumnWidth={getColumnWidth}
          {...columnSize('scheduled')}
        />
      ),
      format: (_, activity) => {
        const { created } = activity;
        if (!created) {
          return <TextEllipsis>{NO_VALUE}</TextEllipsis>;
        }

        return (
          <DateTimeInputWrapper>
            <DateTimeInputInline
              inModal
              readOnly={true}
              value={new Date(created)}
              showDateTooltip
            />
          </DateTimeInputWrapper>
        );
      },
    },
    client: {
      headCell: (
        <SortableHeadCell
          {...columnSize('client')}
          label={getLabel('client', t('Client'))}
          by={contactSortBy}
          onResizeColumnWidth={onResizeColumnWidth}
          onResizeStop={onResizeStop}
          getColumnWidth={getColumnWidth}
          resizable={canEdit}
        />
      ),
      cell: (
        <SizedCell getColumnWidth={getColumnWidth} {...columnSize('client')} />
      ),
      format: (value) => {
        if (!value) {
          return <TextEllipsis>{NO_VALUE}</TextEllipsis>;
        }

        return (
          <TextEllipsisWithTooltip
            type="link"
            to={`/client/${value.id}/details`}
            target="_blank"
          >
            {value.fullName || NO_VALUE}
          </TextEllipsisWithTooltip>
        );
      },
    },
  };
};
const getDefaultLoggedColumns = ({
  onUpdateActivity,
  onResizeColumnWidth,
  onResizeStop,
  getColumnWidth,
  contactSortBy,
  t,
  canEdit = true,
  columnSettings,
}) => {
  const customLabels = columnSettings?.columns?.reduce((acc, column) => {
    if (
      column.id === 'loggedAt' ||
      column.id === 'loggedBy' ||
      column.id === 'scheduledOn'
    ) {
      acc[column.id] = column.label;
      return acc;
    }
    return acc;
  }, {});

  const getLabel = (id, defaultLabel) => {
    return customLabels?.[id] || defaultLabel;
  };
  return {
    loggedAt: {
      headCell: (
        <SortableHeadCell
          by="logged_at"
          numeric
          {...columnSize('loggedAt')}
          label={getLabel('loggedAt', t('Logged At'))}
          onResizeColumnWidth={onResizeColumnWidth}
          onResizeStop={onResizeStop}
          getColumnWidth={getColumnWidth}
          resizable={canEdit}
        />
      ),
      cell: (
        <SizedCell
          getColumnWidth={getColumnWidth}
          {...columnSize('loggedAt')}
        />
      ),
      format: (_, activity) => {
        const { loggedAt } = activity;
        if (!loggedAt) {
          return <TextEllipsis>{NO_VALUE}</TextEllipsis>;
        }

        return (
          <DateTimeInputWrapper>
            <DateTimeInputInline
              inModal
              readOnly={true}
              value={new Date(loggedAt)}
              showDateTooltip
            />
          </DateTimeInputWrapper>
        );
      },
    },
    loggedBy: {
      headCell: (
        <SortableHeadCell
          by="logged_by"
          {...columnSize('scheduled')}
          onResizeColumnWidth={onResizeColumnWidth}
          onResizeStop={onResizeStop}
          getColumnWidth={getColumnWidth}
          label={getLabel('loggedBy', t('Logged By'))}
          resizable={canEdit}
        />
      ),
      cell: (
        <SizedCell
          getColumnWidth={getColumnWidth}
          {...columnSize('scheduled')}
        />
      ),
      format: (_, activity) => {
        const { loggedBy } = activity;
        const fullName = activity.role
          ? activity.role?.name
          : loggedBy?.displayName;
        const imageURL = loggedBy?.pictureUrl || null;

        if (!fullName) {
          return NO_VALUE;
        }

        return (
          <>
            <Hoverable>
              <BubbleProfileImage
                fullName={fullName}
                imageURL={imageURL}
                showToolTip
                popperConfig={{
                  modifiers: {
                    offset: { offset: '0, -5px' }, // Touch top of text
                    preventOverflow: { enabled: false },
                  },
                }}
                value={activity.role || loggedBy || {}}
                activity={{
                  ...activity,
                  access: {
                    view: true,
                    edit: false,
                  },
                }}
                onUpdateActivity={onUpdateActivity}
              />
            </Hoverable>
          </>
        );
      },
    },
    scheduledOn: {
      headCell: (
        <SortableHeadCell
          by="scheduled_on"
          numeric
          {...columnSize('scheduledOn')}
          label={getLabel('scheduledOn', t('Scheduled On'))}
          onResizeColumnWidth={onResizeColumnWidth}
          onResizeStop={onResizeStop}
          getColumnWidth={getColumnWidth}
          resizable={canEdit}
        />
      ),
      cell: (
        <SizedCell
          getColumnWidth={getColumnWidth}
          {...columnSize('scheduledOn')}
        />
      ),
      format: (_, activity) => {
        const { scheduledOn } = activity;
        if (!scheduledOn) {
          return <TextEllipsis>{NO_VALUE}</TextEllipsis>;
        }

        return (
          <DateTimeInputWrapper>
            <DateTimeInputInline
              inModal
              readOnly={true}
              value={new Date(scheduledOn)}
              showDateTooltip
            />
          </DateTimeInputWrapper>
        );
      },
    },
    originalDueDatetime: {
      headCell: (
        <SortableHeadCell
          by="original_due_datetime"
          numeric
          {...columnSize('scheduled')}
          label={t('Original Due Date')}
          onResizeColumnWidth={onResizeColumnWidth}
          onResizeStop={onResizeStop}
          getColumnWidth={getColumnWidth}
          narrowestWidth="165"
          resizable={canEdit}
        />
      ),
      cell: (
        <SizedCell
          getColumnWidth={getColumnWidth}
          {...columnSize('scheduled')}
        />
      ),
      format: (_, activity) => {
        const { originalDueDatetime } = activity;
        if (!originalDueDatetime) {
          return <TextEllipsis>{NO_VALUE}</TextEllipsis>;
        }
        return (
          <DateTimeInputWrapper>
            <DateTimeInputInline
              inModal
              readOnly
              value={new Date(originalDueDatetime)}
              showDateTooltip
            />
          </DateTimeInputWrapper>
        );
      },
    },
    client: {
      headCell: (
        <SortableHeadCell
          {...columnSize('client')}
          label={getLabel('client', t('Contact'))}
          by={contactSortBy}
          onResizeColumnWidth={onResizeColumnWidth}
          onResizeStop={onResizeStop}
          getColumnWidth={getColumnWidth}
          resizable={canEdit}
        />
      ),
      cell: (
        <SizedCell getColumnWidth={getColumnWidth} {...columnSize('client')} />
      ),
      format: (value) => {
        if (!value) {
          return <TextEllipsis>{NO_VALUE}</TextEllipsis>;
        }

        return (
          <TextEllipsisWithTooltip
            type="link"
            to={`/client/${value.id}/details`}
            target="_blank"
          >
            {value.fullName || NO_VALUE}
          </TextEllipsisWithTooltip>
        );
      },
    },
  };
};
const getDueDateBusinessTime = ({
  onUpdateActivity,
  onResizeColumnWidth,
  onResizeStop,
  getColumnWidth,
  timezone,
  t,
  canEdit = true,
}) => ({
  headCell: (
    <SortableHeadCell
      by="due_datetime"
      numeric
      {...columnSize('scheduled')}
      label={t('Due Date (Business Time)')}
      onResizeColumnWidth={onResizeColumnWidth}
      onResizeStop={onResizeStop}
      getColumnWidth={getColumnWidth}
      narrowestWidth="165"
      withTooltip
      resizable={canEdit}
    />
  ),
  cell: (
    <SizedCell getColumnWidth={getColumnWidth} {...columnSize('scheduled')} />
  ),
  format: (_, activity) => {
    const { dueDatetime, access } = activity;
    const dateString = new Date(dueDatetime).toLocaleString('en-US', {
      timeZone: timezone,
    });

    if (!dueDatetime) {
      return <TextEllipsis>{NO_VALUE}</TextEllipsis>;
    }

    return (
      <DateTimeInputWrapper>
        <DateTimeInputInline
          inModal
          readOnly={!(access && access.edit)}
          value={dateString}
          onSubmit={(val) => {
            const zonedDate = toDateInTimezone(val, timezone);

            onUpdateActivity(activity, {
              dueDatetime: zonedDate.toISOString(),
            });
          }}
          dateMenuProps={{
            menuRightButton: <ApplySelectOverlayButton />,
          }}
          timeMenuProps={{
            menuRightButton: <ApplySelectOverlayButton />,
          }}
          showDateTooltip
        />
      </DateTimeInputWrapper>
    );
  },
});

export const createScheduledColumns = (
  {
    showNotesId,
    onSelectAction,
    onUpdateActivity,
    columnSettings,
    objectsById,
    onResizeColumnWidth,
    onResizeStop,
    getColumnWidth,
    contactSortBy,
    timezone,
    canEdit = true,
    showColumnEditorIcon = true,
    searchTerm,
    setSearchTerm,
    handlePersistSearch,
    persistentSearchValue,
    virtualized = false,
  },
  t
) => {
  const defaultColumns = getDefaultScheduledColumns({
    onUpdateActivity,
    onResizeColumnWidth,
    onResizeStop,
    getColumnWidth,
    contactSortBy,
    timezone,
    t,
    canEdit,
    handlePersistSearch,
    persistentSearchValue,
    columnSettings,
  });

  return [
    {
      id: 'overdue',
      headCell: (
        <SizedCell {...columnSize('overdue')} padding="0px">
          <span />
        </SizedCell>
      ),
      cell: (
        <SizedCell align="center" {...columnSize('overdue')} padding="0px" />
      ),
      format: (_, { dueDatetime }) => {
        const scheduledDate = new Date(dueDatetime);

        if (scheduledDate >= Date.now()) {
          return null;
        }

        return (
          <OverlayTrigger
            overlay={<Tooltip label={t('Past Due')} />}
            popperConfig={{
              modifiers: {
                offset: { offset: '0, -50%' }, // Center on top of target
                preventOverflow: { enabled: false },
              },
            }}
          >
            <Hoverable
              as={Icon}
              icon="circle"
              size="1x"
              color="var(--color-button-warning-default)"
              aria-label="Overdue"
            />
          </OverlayTrigger>
        );
      },
    },
    ...((columnSettings && columnSettings.columns) || [])
      .filter(({ visible }) => visible)
      .map(({ id, label, fieldId, objectId }) => {
        if (defaultColumns[id]) {
          return {
            id,
            label,
            ...defaultColumns[id],
          };
          // this field is not in the default columns,
          // so we need to handle it differently
        } else if (id === 'dueDateBusinessTime') {
          return {
            id,
            label,
            ...getDueDateBusinessTime({
              onUpdateActivity,
              onResizeColumnWidth,
              onResizeStop,
              getColumnWidth,
              timezone,
              t,
            }),
          };
        }
        return {
          id,
          label,
          headCell: (
            <SortableHeadCell
              {...columnSize('related')}
              label={label}
              by={getRelatedParam(id, fieldId)}
              onResizeColumnWidth={onResizeColumnWidth}
              onResizeStop={onResizeStop}
              getColumnWidth={getColumnWidth}
              resizable={canEdit}
              fieldId={fieldId}
              objectId={objectId}
              objectsById={objectsById}
            />
          ),
          cell: (
            <SizedCell
              getColumnWidth={getColumnWidth}
              {...columnSize('related')}
            />
          ),
          format: (_, activity) => {
            const val = activity.associatedEntities.find(
              (ae) => ae.customObjectId === id || ae.entityId === fieldId
            );
            const fieldData = activity.associatedFields.find(
              (af) => af.fieldId === fieldId
            );

            if (fieldData) {
              const object = objectsById?.[objectId];
              const { fieldsById = {} } = object || {};
              const field = fieldsById[fieldId];
              const Cell = getCellForFieldType(field?.fieldType);
              const isClient = object?.fetchUrl === 'client';

              const fetchUrl = isClient
                ? getObjectUrl('contacts')
                : `custom-objects/${objectId}`;

              const serviceToUse = isStandardObject(object ?? {})
                ? CustomObjectsService
                : PipelineService;

              const fieldProps =
                field?.fieldType === FIELD_TYPES.Relationship.type &&
                field.relation.fetchUrl === 'client'
                  ? { categorizedFields: [] }
                  : {};

              const value = getFieldDataValue(fieldData, field);

              if (!object || !value) {
                return <TextEllipsis>{NO_VALUE}</TextEllipsis>;
              }

              return (
                <Cell
                  value={value}
                  entityAccess={{ view: true, edit: false }}
                  field={field}
                  fetchUrl={fetchUrl}
                  serviceToUse={serviceToUse}
                  disabled
                  forceReadOnly
                  isCustomObjects={!isClient}
                  fieldProps={fieldProps}
                  displayMaskedValue
                />
              );
            }

            if (!val) {
              return <TextEllipsis>{NO_VALUE}</TextEllipsis>;
            }

            return (
              <TextEllipsisWithTooltip
                type="link"
                to={`/custom-objects/${val.customObjectId}/${val.entityId}/details`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {val.displayName || NO_VALUE}
              </TextEllipsisWithTooltip>
            );
          },
        };
      }),

    {
      id: 'controls',
      headCell: (
        <ColumnsIconCell
          {...columnSize('controls')}
          zIndex="2"
          minWidth="56"
          right={-2}
          canEdit={canEdit && showColumnEditorIcon}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          data-qa="icon-cell"
        />
      ),
      cell: (
        <ControlsCell
          {...columnSize('controls')}
          showNotesId={showNotesId}
          onSelectAction={onSelectAction}
          virtualized={virtualized}
        />
      ),
    },
  ].filter(Boolean);
};

export const createLoggedColumns = (
  {
    onSelectAction,
    onUpdateActivity,
    columnSettings,
    objectsById,
    onResizeColumnWidth,
    onResizeStop,
    getColumnWidth,
    contactSortBy,
    timezone,
    canEdit = true,
    showColumnEditorIcon = true,
    searchTerm,
    setSearchTerm,
    handlePersistSearch,
    persistentSearchValue,
    fields,
    relatedObjects,
    activity,
  },
  t
) => {
  const defaultColumns = getDefaultLoggedColumns({
    onUpdateActivity,
    onResizeColumnWidth,
    onResizeStop,
    getColumnWidth,
    contactSortBy,
    timezone,
    t,
    canEdit,
    handlePersistSearch,
    persistentSearchValue,
    columnSettings,
  });
  return [
    {
      id: 'overdue',
      headCell: (
        <SizedCell {...columnSize('overdueLogged')} padding="0px">
          <span />
        </SizedCell>
      ),
      cell: (
        <SizedCell
          align="center"
          {...columnSize('overdueLogged')}
          padding="0px"
        />
      ),
    },
    ...((columnSettings && columnSettings.columns) || [])
      .filter(({ visible }) => visible)
      .map(({ id, label, fieldId, objectId, ...rest }) => {
        if (defaultColumns[id]) {
          return {
            id,
            label,
            ...defaultColumns[id],
          };
        }
        const activityField = fields.find((f) => f.id === id);
        const custom_object = relatedObjects.find((f) => f.id === id);
        return {
          id,
          label,
          headCell: (
            <SortableHeadCell
              hasSorting={activityField?.id === undefined}
              label={label}
              activityField={activityField}
              by={getSearchByParam(
                activityField?.id,
                custom_object?.id,
                fieldId || id
              )}
              onResizeColumnWidth={onResizeColumnWidth}
              onResizeStop={onResizeStop}
              getColumnWidth={getColumnWidth}
              resizable={canEdit}
              fieldId={fieldId}
              objectId={objectId}
              objectsById={objectsById}
              {...columnSize('related')}
            />
          ),
          cell: (
            <SizedCell
              getColumnWidth={getColumnWidth}
              {...columnSize('related')}
            />
          ),
          format: (_, activity) => {
            const val = activity.associatedEntities.find(
              (ae) => ae.customObjectId === id || ae.entityId === fieldId
            );
            const fieldData =
              activity?.associatedFields &&
              activity.associatedFields.find((af) => af.fieldId === fieldId);

            const fieldsWithValues = activity?.fieldsWithValues[id];

            if (fieldData) {
              const object = objectsById?.[objectId];
              const { fieldsById = {} } = object || {};
              const field = fieldsById[fieldId];
              const Cell = getCellForFieldType(field?.fieldType);
              const isClient = object?.fetchUrl === 'client';

              const fetchUrl = isClient
                ? getObjectUrl('contacts')
                : `custom-objects/${objectId}`;

              const serviceToUse = isStandardObject(object ?? {})
                ? CustomObjectsService
                : PipelineService;

              const fieldProps =
                field?.fieldType === FIELD_TYPES.Relationship.type &&
                field.relation.fetchUrl === 'client'
                  ? { categorizedFields: [] }
                  : {};

              const value = getFieldDataValue(fieldData, field);

              if (!object || !value) {
                return <TextEllipsis>{NO_VALUE}</TextEllipsis>;
              }

              return (
                <Cell
                  value={value}
                  onSubmit={() => {}}
                  entityAccess={{ view: true, edit: false }}
                  object={object}
                  model={object}
                  field={{ ...field, isReadyOnly: true }}
                  fetchUrl={fetchUrl}
                  serviceToUse={serviceToUse}
                  disabled
                  forceReadOnly
                  isCustomObjects={!isClient}
                  fieldProps={fieldProps}
                  displayMaskedValue
                />
              );
            } else if (fieldsWithValues && fields) {
              const field = fields.find((ae) => ae.id === id);
              const fieldType =
                fieldsWithValues.field?.customObjectField.fieldType ??
                fieldsWithValues?.fieldType;
              const Cell = getCellForFieldType(fieldType);
              const object = field?.relation?.object ?? {};
              const customObject = field?.customObjectField?.customObject ?? {};
              const isClient = field?.relation?.fetchUrl === 'client';
              const objectIdForUrl = customObject?.id;
              const fetchUrl = objectIdForUrl
                ? customObject?.fetchUrl === 'client'
                  ? getObjectUrl('contacts')
                  : `custom-objects/${objectIdForUrl}`
                : null;

              const serviceToUse = isStandardObject(object ?? {})
                ? CustomObjectsService
                : PipelineService;

              const fieldProps =
                field?.fieldType === FIELD_TYPES.Relationship.type &&
                field.relation.fetchUrl === 'client'
                  ? { categorizedFields: [] }
                  : {};

              const value = getFieldDataValue(fieldsWithValues, {
                ...field,
                fieldType: fieldType,
              });
              if (!value) {
                return <TextEllipsis>{NO_VALUE}</TextEllipsis>;
              }

              const objectWithPermissions = {
                ...object,
                access: {
                  view: true,
                  edit: false,
                },
              };
              const fieldWithPermissions = {
                ...field,
                fieldType:
                  field.fieldType === 'activity_custom_field' &&
                  fieldType === 'dynamictags'
                    ? 'dynamictags'
                    : field.fieldType,
                access: {
                  view: true,
                  edit: false,
                },
                isReadyOnly: true,
              };
              return (
                <Cell
                  value={value}
                  onSubmit={() => {}}
                  entityAccess={{ view: true, edit: false }}
                  object={objectWithPermissions}
                  model={object}
                  field={fieldWithPermissions}
                  fetchUrl={fetchUrl}
                  serviceToUse={serviceToUse}
                  disabled
                  forceReadOnly
                  isCustomObjects={!isClient}
                  fieldProps={fieldProps}
                  displayMaskedValue
                />
              );
            }
            if (!val) {
              return <TextEllipsis>{NO_VALUE}</TextEllipsis>;
            }

            return (
              <TextEllipsisWithTooltip
                type="link"
                to={`/custom-objects/${val.customObjectId}/${val.entityId}/details`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {val.displayName || NO_VALUE}
              </TextEllipsisWithTooltip>
            );
          },
        };
      }),

    {
      id: 'controls',
      headCell: (
        <ColumnsIconCell
          {...columnSize('controls')}
          zIndex="2"
          minWidth="56"
          right={-2}
          canEdit={canEdit && showColumnEditorIcon}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          data-qa="icon-cell"
        />
      ),
      cell: (
        <ControlsCell
          {...columnSize('controls')}
          onSelectAction={onSelectAction}
          activityObject={activity}
          tableType="logged"
          virtualized={true}
          showIconButton={false}
        />
      ),
    },
  ].filter(Boolean);
};
